import {
  CoeProductPositionEntity,
  FinanceResponsesCriptoProductPosition,
  FixedIncomeProductPositionEntity,
  FundsProductNotesEntity,
  PensionProductPositionEntity,
  ResponsesConsolidatedPositionFilterResponse,
  ResponsesEquityProductBmfResponse,
  ResponsesEquityProductInCashResponse,
  ResponsesEquityProductOptionResponse,
  ResponsesEquityProductTermResponse,
  ResponsesFundsProductResponse,
  ResponsesRecommendedPortfolioSummaryResponse,
  ResponsesTreasuryProductResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { capitalize } from 'lodash';

import { ColorCodedTextProps } from '../../components/ColorCodedText';

import {
  AssetsFilterProps,
  ClubsAssetsQueryItem,
  CoeAssetsQueryItem,
  CryptoAssetsQueryItem,
  DirectTreasuryAssetsQueryItem,
  EquityAssetsQueryItem,
  EquityProductPositionEntity,
  FixedIncomeAssetsQueryItem,
  FundsAssetsNoteItem,
  FundsAssetsQueryItem,
  PrivatePensionAssetsQueryItem,
} from './types';

const normalizeVariationType = (variation: number): ColorCodedTextProps['sign'] => {
  if (variation > 0) {
    return 'positive';
  }

  if (variation < 0) {
    return 'negative';
  }

  return 'neutral';
};

export const normalizeFundsAssets = (
  assets: ResponsesFundsProductResponse[] | null | undefined
): FundsAssetsQueryItem[] => {
  if (!assets) {
    return [];
  }

  const normalizeNotes = (
    notes: FundsProductNotesEntity[] | null | undefined
  ): FundsAssetsNoteItem[] => {
    if (!notes) {
      return [];
    }

    return notes.map((note) => ({
      ...note,
      implementationDateNormalized: note.implementationDate
        ? Utils.Mask.DateFormat(note.implementationDate, 'dd/MM/yyyy')
        : '--',
      historicalValueNormalized: Utils.Mask.Money(note.historicalValue || 0),
      quotaAmountNormalized: Utils.Mask.Money(note.quotaAmount || 0),
      quotaPurchasedValueNormalized: Utils.Mask.Money(note.quotaPurchasedValue || 0),
      grossValueNormalized: Utils.Mask.Money(note.grossValue || 0),
      iofValueNormalized: Utils.Mask.Money(note.iofValue || 0),
      irValueNormalized: Utils.Mask.Money(note.irValue || 0),
      netValueNormalized: Utils.Mask.Money(note.netValue || 0),
    }));
  };

  return assets.map((asset) => ({
    ...asset,
    productNameNormalized: asset.fundName || '--',
    productTypeNormalized: asset.category || '--',
    positionDateNormalized: asset.positionDate
      ? Utils.Mask.DateFormat(asset.positionDate, 'dd/MM/yyyy')
      : '--',
    quotaCurrentValueNormalized: Utils.Mask.Money(asset.quotaCurrentValue || 0),
    valueNormalized: Utils.Mask.Money(asset.grossFinancialValue || 0),
    variationNormalized: Utils.Mask.Percentage(asset.totalVariation),
    variationType: normalizeVariationType(asset.totalVariation || 0),
    notesNormalized: normalizeNotes(asset.notes),
    allowWithdrawNormalized: !!asset.allowWithdraw,
  }));
};

export const normalizeFixedIncomeAssets = (
  assets: FixedIncomeProductPositionEntity[] | null | undefined
): FixedIncomeAssetsQueryItem[] => {
  if (!assets) {
    return [];
  }

  const normalizeQuote = (quote: FixedIncomeProductPositionEntity['quote']) => {
    if (!quote) {
      return undefined;
    }

    if (quote.status === 'PendingClient' || quote.status === 'Settled') {
      return quote;
    }

    return undefined;
  };

  return assets.map((asset) => ({
    ...asset,
    uniqueCodeNormalized: Number(asset.uniqueCode),
    productNameNormalized: asset.issuerFullName || '--',
    productTypeNormalized: asset.category || '--',
    taxNormalized: asset.taxText || '--',
    availableQuantityNormalized: asset.availableAmount || 0,
    blockedQuantityNormalized: asset.blockedAmount || 0,
    quantityNormalized: asset.amount || 0,
    maturityDateNormalized: asset.dueDate
      ? Utils.Mask.DateFormat(asset.dueDate, 'dd/MM/yyyy')
      : '--',
    valueNormalized: Utils.Mask.Money(asset.grossFinancialValue || 0),
    variationNormalized: Utils.Mask.Percentage(asset.variation),
    variationType: normalizeVariationType(asset.variation || 0),
    productCodeNormalized: asset.selicSetipCode || '--',
    buyDateNormalized: asset.buyDate ? Utils.Mask.DateFormat(asset.buyDate, 'dd/MM/yyyy') : '--',
    iofNormalized: Utils.Mask.Money(asset.iof || 0),
    irNormalized: Utils.Mask.Money(asset.ir || 0),
    purchaseValueNormalized: Utils.Mask.Money(asset.buyUnitaryPrice || 0),
    netValueNormalized: Utils.Mask.Money(asset.netFinancialValue || 0),
    quoteNormalized: normalizeQuote(asset.quote),
    pricingMethod: asset.originDescription ? capitalize(asset.originDescription) : '',
    isCurvePricingMethod: asset.originDescription?.toLowerCase() === 'curva',
    curvePUNormalized: asset.currentCurveUnitaryPrice?.toString() || '--',
    curveValueNormalized: asset.curveGrossFinancialValue
      ? Utils.Mask.Money(asset.curveGrossFinancialValue)
      : '--',
    curveVariationNormalized: Utils.Mask.Percentage(asset.curveVariation),
    marketPUNormalized: asset.currentMarketUnitaryPrice?.toString() || '--',
    marketValueNormalized: asset.marketGrossFinancialValue
      ? Utils.Mask.Money(asset.marketGrossFinancialValue)
      : '--',
    marketVariationNormalized: Utils.Mask.Percentage(asset.marketVariation),
  }));
};

export const normalizeDirectTreasuryAssets = (
  assets: ResponsesTreasuryProductResponse[] | null | undefined
): DirectTreasuryAssetsQueryItem[] => {
  if (!assets) {
    return [];
  }

  const normalizeQuantity = (quantity: number | null | undefined): string => {
    if (!quantity) {
      return '0';
    }

    return quantity.toFixed(2);
  };

  return assets.map((asset) => ({
    ...asset,
    productNameNormalized: asset.titleName || '--',
    productTypeNormalized: asset.category || '--',
    availableQuantityNormalized: normalizeQuantity(asset.availableAmount),
    blockedQuantityNormalized: normalizeQuantity(asset.blockedAmount),
    totalQuantityNormalized: normalizeQuantity(asset.amount),
    expirationDateNormalized: asset.dueDate
      ? Utils.Mask.DateFormat(asset.dueDate, 'dd/MM/yyyy')
      : '--',
    valueNormalized: Utils.Mask.Money(asset.grossFinancialValue || 0),
  }));
};

export const normalizeCryptoAssets = (
  assets: FinanceResponsesCriptoProductPosition[] | null | undefined
): CryptoAssetsQueryItem[] => {
  if (!assets) {
    return [];
  }

  return assets.map((asset) => ({
    ...asset,
    productNameNormalized: asset.assetName || '--',
    quantityNormalized: asset.amount || 0,
    positionNormalized: Utils.Mask.Money(asset.grossFinancialValue),
    resultNormalized: asset.result ? Utils.Mask.Money(asset.result) : '--',
    variationType: normalizeVariationType(asset.profitability || 0),
    profitabilityNormalized: asset.profitability
      ? Utils.Mask.Percentage(asset.profitability)
      : '--',
    valueNormalized: Utils.Mask.Money(asset.unitPrice),
    averagePriceNormalized: Utils.Mask.Money(asset.averagePrice),
  }));
};

export const extractPortfolioAssetsHashMap = (
  recommendedPortfolios: ResponsesRecommendedPortfolioSummaryResponse[] | null | undefined
): Map<string, string[]> => {
  const portfolioAssetsHashMap = new Map<string, string[]>();

  if (recommendedPortfolios) {
    recommendedPortfolios.forEach((portfolio) => {
      if (portfolio.assets) {
        portfolio.assets
          .filter((asset) => !!asset)
          .forEach((asset) => {
            const existingPortfolios = portfolioAssetsHashMap.get(asset) || [];
            existingPortfolios.push(portfolio.portfolioName || '');
            portfolioAssetsHashMap.set(asset, existingPortfolios);
          });
      }
    });
  }

  return portfolioAssetsHashMap;
};

export const normalizeEquityAssets = (
  assetsInCash: ResponsesEquityProductInCashResponse[] | null | undefined,
  assetsTerm: ResponsesEquityProductTermResponse[] | null | undefined,
  assetsOption: ResponsesEquityProductOptionResponse[] | null | undefined,
  assetsBmf: ResponsesEquityProductBmfResponse[] | null | undefined,
  recommendedPortfolios: ResponsesRecommendedPortfolioSummaryResponse[] | null | undefined
): EquityAssetsQueryItem[] => {
  const assets: EquityProductPositionEntity[] = [];

  const extractAssets = (
    assetsToExtract:
      | ResponsesEquityProductInCashResponse[]
      | ResponsesEquityProductTermResponse[]
      | ResponsesEquityProductOptionResponse[]
      | ResponsesEquityProductBmfResponse[]
      | null
      | undefined
  ) => {
    if (assetsToExtract) {
      assets.push(...assetsToExtract);
    }
  };

  extractAssets(assetsInCash);
  extractAssets(assetsTerm);
  extractAssets(assetsOption);
  extractAssets(assetsBmf);

  if (!assets) {
    return [];
  }

  const portfolioAssetsHashMap = extractPortfolioAssetsHashMap(recommendedPortfolios);

  return assets.map((asset) => ({
    ...asset,
    productNameNormalized: asset.assetCode || '--',
    productCategoryNormalized: asset.category || '--',
    productTypeNormalized: asset.marketDescription || '--',
    availableQuantityNormalized: asset.availableAmount || 0,
    blockedQuantityNormalized: asset.blockedAmount || 0,
    totalQuantityNormalized: asset.amount || 0,
    averagePriceNormalized: Utils.Mask.Money(asset.averageUpdatedPrice || 0),
    actualPriceNormalized: Utils.Mask.Money(asset.updatedPrice || 0),
    resultNormalized: Utils.Mask.Money(asset.result || 0),
    variationNormalized: Utils.Mask.Percentage(asset.lastPriceVariation),
    variationType: normalizeVariationType(asset.lastPriceVariation || 0),
    valueNormalized: Utils.Mask.Money(asset.grossFinancialValue || 0),
    hasRecommendedPortfolioLink: !!asset.assetCode && portfolioAssetsHashMap.has(asset.assetCode),
    recommendedPortfolioLinks: asset.assetCode
      ? portfolioAssetsHashMap.get(asset.assetCode) || []
      : [],
  }));
};

export const normalizePrivatePensionAssets = (
  assets: PensionProductPositionEntity[] | null | undefined
): PrivatePensionAssetsQueryItem[] => {
  if (!assets) {
    return [];
  }

  return assets.map((asset) => ({
    ...asset,
    productNameNormalized: asset.fundName || '--',
    positionDateNormalized: asset.positionDate
      ? Utils.Mask.DateFormat(asset.positionDate, 'dd/MM/yyyy')
      : '--',
    planNormalized: asset.planType || '--',
    investmentTaxNormalized: asset.taxRegime || '--',
    dayNormalized: Utils.Mask.Money(asset.dailyValue || 0),
    monthNormalized: Utils.Mask.Money(asset.monthValue || 0),
    yearNormalized: Utils.Mask.Money(asset.annualValue || 0),
    valueNormalized: Utils.Mask.Money(asset.grossFinancialValue || 0),
  }));
};

export const normalizeClubsAssets = (
  assets: ResponsesFundsProductResponse[] | null | undefined
): ClubsAssetsQueryItem[] => {
  return normalizeFundsAssets(assets);
};

export const normalizeCoeAssets = (
  assets: CoeProductPositionEntity[] | null | undefined
): CoeAssetsQueryItem[] => {
  if (!assets) {
    return [];
  }

  return assets.map((asset) => ({
    ...asset,
    operationNormalized: asset.operationName || '--',
    productNameNormalized: asset.assetCode || '--',
    expirationDateNormalized: asset.dueDate
      ? Utils.Mask.DateFormat(asset.dueDate, 'dd/MM/yyyy')
      : '--',
    appliedValueNormalized: Utils.Mask.Money(asset.investedValue || 0),
    returnPercentageNormalized: Utils.Mask.Percentage(asset.assetResponse || 0),
    markingValueNormalized: Utils.Mask.Money(asset.grossFinancialValue || 0),
  }));
};

export const normalizeAssetsFilters = (
  filters?: ResponsesConsolidatedPositionFilterResponse[] | null
): AssetsFilterProps[] => {
  if (!filters) {
    return [];
  }

  return [
    {
      label: 'Todos',
      value: '',
    },
    ...filters.map((filter) => ({
      value: filter.filterValue || '--',
      label: filter.filterDescription || '--',
    })),
  ];
};
